import { useMemo } from "react";
import { defineMessages, useIntl, type MessageDescriptor } from "react-intl";
import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";

import { past } from "@aglocal/web/helpers/time";

import Callout, { CalloutProps } from "@aglocal/web/components/Callout";

const messages = defineMessages({
  header: {
    defaultMessage: "Action Items ({count})",
    description:
      "Header of the in-meeting sidebar section listing actions the recruiter should take; {count} is the number of actions.",
  },
  noResumeTitle: {
    defaultMessage: "We currently do not have a resume.",
    description:
      "Action item title shown when the talent has no resume on file.",
  },
  noResumeBody: {
    defaultMessage: "Please ask them to send one.",
    description:
      "Action item detail asking the recruiter to request a resume from the talent.",
  },
  monthOldResumeTitle: {
    defaultMessage: "The resume we have is over a month old.",
    description:
      "Action item title shown when the talent's resume is older than a month.",
  },
  monthOldResumeBody: {
    defaultMessage: "Check that there are no changes in employment.",
    description:
      "Action item detail asking the recruiter to confirm the talent's employment is unchanged.",
  },
  yearOldResumeTitle: {
    defaultMessage: "The resume we have is over a year old.",
    description:
      "Action item title shown when the talent's resume is older than a year.",
  },
  yearOldResumeBody: {
    defaultMessage: "Please ask them to send a new one.",
    description:
      "Action item detail asking the recruiter to request an updated resume from the talent.",
  },
});

interface Action {
  key: string;
  intent: CalloutProps["intent"];
  title: MessageDescriptor;
  body: MessageDescriptor;
}

function getResumeAction({ interviewee }: Interview): Action | null {
  const connectedDetails = interviewee?.connectedDetails;

  if (!connectedDetails?.latestResumeDate) {
    return {
      key: "resume",
      intent: "error",
      title: messages.noResumeTitle,
      body: messages.noResumeBody,
    };
  }

  const resumeDate = new Date(connectedDetails.latestResumeDate);
  if (resumeDate < past({ months: 1 })) {
    return {
      key: "resume",
      intent: "warning",
      title: messages.monthOldResumeTitle,
      body: messages.monthOldResumeBody,
    };
  }

  if (resumeDate < past({ years: 1 })) {
    return {
      key: "resume",
      intent: "error",
      title: messages.yearOldResumeTitle,
      body: messages.yearOldResumeBody,
    };
  }

  return null;
}

export interface ActionItemsAccordionItemProps {
  name: unknown;
  interview: Interview;
}

export default function ActionItemsAccordionItem({
  name,
  interview,
}: ActionItemsAccordionItemProps) {
  const { $t } = useIntl();
  const items = useMemo<Action[]>(() => {
    const resumeAction = getResumeAction(interview);
    return resumeAction ? [resumeAction] : [];
  }, [interview]);

  return (
    <AccordionItem value={name}>
      <AccordionHeader>
        {$t(messages.header, { count: items.length })}
      </AccordionHeader>
      <AccordionPanel>
        {items.map(({ key, intent, title, body }) => (
          <Callout key={key} intent={intent} title={$t(title)}>
            {$t(body)}
          </Callout>
        ))}
      </AccordionPanel>
    </AccordionItem>
  );
}
